import { supabase } from "@/integrations/supabase/client";
import { getCachedScrapingEnabled } from "@/hooks/useScrapingEnabled";
import { todayBrazilISO } from "@/lib/brazilTime";

// Endpoints that should be logged for scraping detection
const MONITORED_ENDPOINTS = [
  "arsenal_videos",
  "arsenal_creators",
  "marketplace_products",
  "community_posts",
  "ensino_courses",
  "ensino_lessons",
  "profiles",
];

let sessionId: string | null = null;

function getSessionId(): string {
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }
  return sessionId;
}

function isMonitoredEndpoint(endpoint: string): boolean {
  return MONITORED_ENDPOINTS.some((e) => endpoint.includes(e));
}

function invokeRequestLog(
  endpoint: string,
  httpMethod: string,
  responseTimeMs?: number
): void {
  supabase.functions
    .invoke("log-request", {
      body: {
        endpoint,
        http_method: httpMethod,
        response_time_ms: responseTimeMs ?? null,
        session_id: getSessionId(),
      },
    })
    .catch(() => {
      // Silently ignore — monitoring should never affect UX
    });
}

/**
 * Fire-and-forget request logger. Call after sensitive data fetches.
 * Does not block the caller or throw errors.
 */
export function logRequest(
  endpoint: string,
  httpMethod: string = "GET",
  responseTimeMs?: number
): boolean {
  // Only log monitored endpoints
  if (!isMonitoredEndpoint(endpoint)) return false;

  // Kill switch — admin can disable scraping monitor entirely
  if (!getCachedScrapingEnabled()) return false;

  // Fire and forget — no await, no error propagation
  invokeRequestLog(endpoint, httpMethod, responseTimeMs);

  return true;
}

export function logToolUsageOncePerDay(
  endpoint: string,
  httpMethod: string = "GET",
  responseTimeMs?: number
): boolean {
  if (!isMonitoredEndpoint(endpoint)) return false;

  const key = `tool_usage_log:v2:${endpoint}:${todayBrazilISO()}`;
  let canUseSessionStorage = false;

  try {
    canUseSessionStorage = typeof sessionStorage !== "undefined";
    if (canUseSessionStorage && sessionStorage.getItem(key)) return false;
  } catch {
    canUseSessionStorage = false;
  }

  invokeRequestLog(endpoint, httpMethod, responseTimeMs);

  if (canUseSessionStorage) {
    try {
      sessionStorage.setItem(key, "1");
    } catch {
      // ignore
    }
  }

  return true;
}
